import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, Globe, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../types/translations';
import { useCategories } from '../hooks/useCategories';
import { useCategoryById } from '../hooks/useCategoryById';
import logo from '../assets/images/logo.png';
import './Navigation.css';

const Navigation: React.FC = () => {
    const { translations, currentLanguage, setLanguage, isRTL } = useLanguage();
    const { categories, loading: categoriesLoading } = useCategories();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const [hoveredCategory, setHoveredCategory] = useState<number | null>(null);
    const [expandedMobileCategory, setExpandedMobileCategory] = useState<number | null>(null);
    const { category: categoryWithSubs } = useCategoryById(hoveredCategory);
    const { category: expandedCategoryWithSubs } = useCategoryById(expandedMobileCategory);
    const location = useLocation();

    const isActive = (path: string) => location.pathname === path;

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const closeMenu = () => {
        setIsMenuOpen(false);
        setExpandedMobileCategory(null);
    };

    const toggleMobileCategory = (categoryId: number) => {
        setExpandedMobileCategory(prev => prev === categoryId ? null : categoryId);
    };

    return (
        <nav className={`navigation ${isRTL ? 'rtl' : 'ltr'}`}>
            <div className="nav-container">
                {/* Logo */}
                <Link to="/" className="nav-logo" onClick={closeMenu}>
                    <img
                        src={logo}
                        alt="Unicare Pharma"
                        className="logo-image"
                    />
                    <span className="logo-title">
                        {isRTL ? 'دوائك عندنا' : 'Dawak Endna'}
                    </span>
                </Link>

                {/* Desktop Menu */}
                <div className="nav-menu desktop-menu">
                    <Link
                        to="/"
                        className={`nav-link ${isActive('/') ? 'active' : ''}`}
                    >
                        {translations.home}
                    </Link>

                    {/* Products Dropdown */}
                    <div
                        className="nav-dropdown hierarchical-menu"
                        onMouseEnter={() => setIsProductsDropdownOpen(true)}
                        onMouseLeave={() => {
                            setIsProductsDropdownOpen(false);
                            setHoveredCategory(null);
                        }}
                    >
                        <button className="nav-link dropdown-trigger">
                            {translations.products}
                            <ChevronDown className={`chevron ${isProductsDropdownOpen ? 'open' : ''}`} size={16} />
                        </button>

                        {isProductsDropdownOpen && (
                            <div className="dropdown-menu main-menu">
                                {/* All Products */}
                                <Link to="/products" className="main-menu-item">
                                    <span>{isRTL ? 'جميع المنتجات' : 'All Products'}</span>
                                </Link>

                                {/* Categories with Submenus */}
                                {!categoriesLoading && categories.map(category => (
                                    <div
                                        key={category.id}
                                        className="main-menu-item-wrapper"
                                        onMouseEnter={() => setHoveredCategory(category.id)}
                                        onMouseLeave={() => setHoveredCategory(null)}
                                    >
                                        <div className="main-menu-item category-item">
                                            <span>{isRTL ? category.name.ar : category.name.en}</span>
                                            <ChevronRight size={14} className={isRTL ? 'flip' : ''} />
                                        </div>

                                        {/* Subcategory Submenu */}
                                        {hoveredCategory === category.id && categoryWithSubs && categoryWithSubs.subcategories && categoryWithSubs.subcategories.length > 0 && (
                                            <div className="submenu">
                                                {categoryWithSubs.subcategories.map(subcategory => (
                                                    <Link
                                                        key={subcategory.id}
                                                        to={`/products/${category.id}?subcategory=${subcategory.id}`}
                                                        className="submenu-item"
                                                    >
                                                        <span>{isRTL ? subcategory.name.ar : subcategory.name.en}</span>
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <Link
                        to="/contact"
                        className={`nav-link ${isActive('/contact') ? 'active' : ''}`}
                    >
                        {translations.contact}
                    </Link>

                    <Link
                        to="/policies"
                        className={`nav-link ${isActive('/policies') ? 'active' : ''}`}
                    >
                        {translations.policies}
                    </Link>
                </div>

                {/* Language Switcher & Mobile Menu Button */}
                <div className="nav-actions">
                    {/* Language Switcher */}
                    <div
                        className="language-switcher"
                        onMouseEnter={() => setIsLanguageDropdownOpen(true)}
                        onMouseLeave={() => setIsLanguageDropdownOpen(false)}
                    >
                        <button className="language-trigger">
                            <Globe size={20} />
                            <span>{currentLanguage.name}</span>
                            <ChevronDown className={`chevron ${isLanguageDropdownOpen ? 'open' : ''}`} size={16} />
                        </button>

                        {isLanguageDropdownOpen && (
                            <div className="language-menu">
                                {languages.map(lang => (
                                    <button
                                        key={lang.code}
                                        onClick={() => setLanguage(lang)}
                                        className={`language-item ${currentLanguage.code === lang.code ? 'active' : ''}`}
                                    >
                                        {lang.name}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button className="mobile-menu-btn" onClick={toggleMenu}>
                        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="mobile-menu">
                        <Link to="/" className="mobile-nav-link" onClick={closeMenu}>
                            {translations.home}
                        </Link>

                        <div className="mobile-products-group">
                            <span className="mobile-group-title">{translations.products}</span>
                            <Link to="/products" className="mobile-nav-link" onClick={closeMenu}>
                                {isRTL ? 'جميع المنتجات' : 'All Products'}
                            </Link>
                            {!categoriesLoading && categories.map(category => (
                                <div key={category.id} className="mobile-category-group">
                                    <div 
                                        className="mobile-nav-link category-link"
                                        onClick={() => toggleMobileCategory(category.id)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {isRTL ? category.name.ar : category.name.en}
                                    </div>
                                    {expandedMobileCategory === category.id && expandedCategoryWithSubs && expandedCategoryWithSubs.subcategories && expandedCategoryWithSubs.subcategories.length > 0 && (
                                        <div className="mobile-subcategory-list">
                                            {expandedCategoryWithSubs.subcategories.map(subcategory => (
                                                <Link
                                                    key={subcategory.id}
                                                    to={`/products/${category.id}?subcategory=${subcategory.id}`}
                                                    className="mobile-nav-link subcategory-link"
                                                    onClick={closeMenu}
                                                >
                                                    {isRTL ? subcategory.name.ar : subcategory.name.en}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <Link to="/contact" className="mobile-nav-link" onClick={closeMenu}>
                            {translations.contact}
                        </Link>

                        <Link to="/policies" className="mobile-nav-link" onClick={closeMenu}>
                            {translations.policies}
                        </Link>
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navigation;